import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

const parseDurationToMs = (duration: string): number | null => {
  if (!duration || duration === "off" || duration === "never") return null;

  // Check for minutes format
  const minutesMatch = duration.match(/^(\d+)\s*minutes?$/i);
  if (minutesMatch) {
    return parseInt(minutesMatch[1], 10) * 60 * 1000;
  }

  // Check for hours format
  const hoursMatch = duration.match(/^(\d+)\s*hours?$/i);
  if (hoursMatch) {
    return parseInt(hoursMatch[1], 10) * 60 * 60 * 1000;
  }

  return null;
};

const DEFAULT_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours

export const useAutoLogout = (): void => {
  const { settings, logout, isAuthenticated } = useAuth();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const duration = settings?.security?.autoLogoutDuration ?? "4 hours";
  const timeoutMs = parseDurationToMs(duration);

  useEffect(() => {
    const cleanup = (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    // Skip if not authenticated or auto-logout is off
    if (!isAuthenticated || timeoutMs === null) {
      return cleanup;
    }

    // Clear any existing timer
    cleanup();

    // Set timer - logout after specified duration
    timeoutRef.current = setTimeout(() => {
      if (logout) {
        logout();
      }
    }, timeoutMs || DEFAULT_TIMEOUT);

    // Cleanup on unmount or when dependencies change
    return cleanup;
  }, [timeoutMs, duration, logout, isAuthenticated]);
};
